/**
 * ChunkBuffer — append-only string buffer with rAF-batched notifications.
 *
 * Pure TypeScript. No React. The streaming pipeline writes chunks in as they
 * arrive on the wire.
 */

import {
  cancelFrame,
  scheduleFrame
} from 'src/content/ChatV2/lib/frameScheduler';

/* Public shape — `useSyncExternalStore(subscribe, getSnapshot)` consumes
 * `subscribe` + `getSnapshot` directly, so keep these names. */
export interface ChunkBuffer {
  append(chunk: string): void;
  reset(): void;
  getSnapshot(): string;
  subscribe(listener: () => void): () => void;
  dispose(): void;
}

export function createChunkBuffer(): ChunkBuffer {
  let buffer = '';
  let pending: string[] = [];
  let frameHandle: number | null = null;
  let disposed = false;
  const listeners = new Set<() => void>();

  const notify = () => {
    for (const listener of [...listeners]) {
      listener();
    }
  };

  const flush = () => {
    frameHandle = null;
    if (pending.length === 0) {
      return;
    }
    buffer += pending.join('');
    pending = [];
    notify();
  };

  return {
    append(chunk) {
      if (disposed || chunk === '') {
        return;
      }
      pending.push(chunk);
      if (frameHandle === null) {
        frameHandle = scheduleFrame(flush);
      }
    },
    reset() {
      if (disposed) {
        return;
      }
      if (frameHandle !== null) {
        cancelFrame(frameHandle);
        frameHandle = null;
      }
      pending = [];
      if (buffer === '') {
        return;
      }
      buffer = '';
      notify();
    },
    getSnapshot() {
      return buffer;
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    dispose() {
      if (disposed) {
        return;
      }
      disposed = true;
      if (frameHandle !== null) {
        cancelFrame(frameHandle);
        frameHandle = null;
      }
      pending = [];
      listeners.clear();
    }
  };
}
